import React, { useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  IconButton,
  Stack,
  Collapse,
  PopoverArrow,
  Popover,
  PopoverTrigger,
  PopoverContent,
  useColorModeValue,
  useDisclosure,
  StackDivider,
  Container,
  Link as ChakraLink,
} from '@chakra-ui/react';
import { HamburgerIcon, CloseIcon, ChevronLeftIcon } from '@chakra-ui/icons';
import { useStaticQuery, graphql, withPrefix } from 'gatsby';
import { useLocation } from '@gatsbyjs/reach-router';
import { Logo } from '../Logo';
import { Link } from '../Link';
import { DownloadLinks } from '../DownloadLinks';

export default function Navigation() {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const currentLocation = useLocation();

  useEffect(() => {
    if (isOpen) {
      document.getElementsByTagName('html')[0].style.overflow = 'hidden';
    } else {
      document.getElementsByTagName('html')[0].style.overflow = 'auto';
    }
    // Close the menu when resizing above the mobile menu breakpoint
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isOpen) {
        onClose(); // Close the menu when resizing above the breakpoint
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, onClose]);

  return (
    <Box as='nav' role='navigation' position='relative'>
      <Flex
        position={{ base: 'relative', lg: 'absolute' }}
        top={0}
        left={0}
        right={0}
        width='100%'
        bg={{ base: 'navy', lg: 'transparent' }}
        px={{ base: 0, md: 20 }}
        py={{ base: 0, md: 15 }}
        justifyContent='space-between'
      >
        <ChakraLink
          href='https://www.imf.org'
          color={{ base: 'white', lg: 'black' }}
          textStyle='navItem'
          backgroundColor={{ base: 'navy', md: 'transparent' }}
          _hover={{
            textDecoration: 'none',
            color: { base: '#81E6D9', lg: 'navy' },
          }}
          display='inline-flex'
          alignItems='center'
          width={{ base: '100%', md: 'auto' }}
          padding={{ base: 20, md: 0 }}
        >
          <ChevronLeftIcon />
          Learn more about the IMF
        </ChakraLink>
        <Box display={{ base: 'none', md: 'block' }} zIndex='2'>
          <DownloadLinks variant='menu' />
        </Box>
      </Flex>
      <Flex alignItems='center' px={{ base: 20, lg: 0 }}>
        <Flex flex={{ base: 1 }} flexDirection='column' justify='start' alignItems='stretch'>
          <Box color='navy' py={{ base: 5, md: 20 }} mx={{ base: '0', md: 'auto' }}>
            <Link
              to='/'
              _hover={{
                textDecoration: 'none',
                opacity: 0.8,
              }}
              py={{ base: 5, lg: 10 }}
              display='block'
            >
              <Logo />
            </Link>
          </Box>

          <Box
            display={{ base: 'none', lg: 'block' }}
            borderTop={1}
            borderStyle='solid'
            borderColor='gray.200'
          >
            <Container
              maxWidth='container.xl'
              margin='auto'
              padding='0'
              zIndex='1'
              position='relative'
              display='flex'
              alignItems='center'
              justifyContent='center'
            >
              <Flex display={{ base: 'none', lg: 'flex' }} ml={20} alignItems='center'>
                <DesktopNav currentLocation={currentLocation} />
              </Flex>
            </Container>
          </Box>
        </Flex>

        <Flex display={{ base: 'flex', lg: 'none' }}>
          <IconButton
            onClick={onToggle}
            icon={isOpen ? <CloseIcon w={3} h={3} /> : <HamburgerIcon w={5} h={5} />}
            variant='transparent'
            aria-label='Toggle Navigation'
            aria-expanded={isOpen}
          />
        </Flex>
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <MobileNav currentLocation={currentLocation} />
      </Collapse>
    </Box>
  );
}

const url = (item) => {
  const { internal, external } = item || {};
  if (internal) {
    if (internal.section === 'single') {
      // Single category pages have URLs at the root of the site
      return `${withPrefix('/')}${internal.slug.current}/`;
    }
    return `${withPrefix('/')}${internal.section}/${internal.slug.current}/`;
  }
  if (external) {
    return external;
  }

  return null;
};

const isActive = (navItem, currentLocation) => {
  if (currentLocation.pathname === url(navItem.url)) {
    return true;
  }

  if (navItem.children) {
    const activeChild = navItem.children.find(
      (child) => currentLocation.pathname === url(child.url),
    );
    if (activeChild) {
      return true;
    }
  }

  const downloadPaths = [
    'french',
    'english',
    'spanish',
    'portuguese',
    'arabic',
    'russian',
    'chinese',
    'japanese',
    'german',
  ];

  if (
    navItem.text === 'Downloads' &&
    downloadPaths.includes(
      currentLocation.pathname.split('/')[currentLocation.pathname.split('/').length - 2],
    )
  ) {
    return true;
  }

  return false;
};

const DesktopNav = ({ currentLocation }) => {
  const data = useStaticQuery(NAVIGATION_QUERY);
  const { items } = data.data;
  const popoverContentBgColor = useColorModeValue('white', 'gray.800');

  return (
    <Stack direction='row' spacing={4} alignItems='center' justifyContent='center'>
      {items.map((navItem) => (
        <Box key={navItem.text} height='100%'>
          <Popover trigger='hover' placement='bottom-start'>
            <PopoverTrigger>
              <Box
                as={navItem.url ? Link : 'button'}
                py={20}
                px={20}
                to={navItem.url}
                textStyle='navItem'
                color={isActive(navItem, currentLocation) ? 'navy' : 'black'}
                bg={isActive(navItem, currentLocation) ? 'white' : 'transparent'}
                textDecoration={isActive(navItem, currentLocation) ? 'underline' : 'none'}
                height='100%'
                display='flex'
                alignItems='center'
                letterSpacing='1px'
                _hover={{
                  textDecoration: 'none',
                  bg: 'lightBlue',
                  color: 'black',
                }}
              >
                {navItem.text}
              </Box>
            </PopoverTrigger>

            {navItem.children.length ? (
              <PopoverContent
                boxShadow='lg'
                border='1px solid rgba(0,0,0,0.15)'
                bg={popoverContentBgColor}
                p={4}
                rounded='xl'
                minW='sm'
                color='black'
              >
                <PopoverArrow bg={popoverContentBgColor} />
                <Stack className='nav nav-desktop'>
                  {navItem.children.map((child) => (
                    <DesktopSubNav key={child.text} currentLocation={currentLocation} {...child} />
                  ))}
                </Stack>
              </PopoverContent>
            ) : null}
          </Popover>
        </Box>
      ))}
    </Stack>
  );
};

const DesktopSubNav = ({ text, url, file, currentLocation }) => (
  <Link
    to={url}
    href={file?.asset?.url && file.asset.url}
    target={file?.asset?.url && '_blank'}
    role='group'
    variant='header'
    textDecoration={isActive({ url }, currentLocation) ? 'underline' : 'none'}
  >
    <Stack direction='row' align='center'>
      <Box>
        <Text transition='all .3s ease'>{text}</Text>
      </Box>
    </Stack>
  </Link>
);

const MobileNavItem = ({ text, url, children, currentLocation }) => (
  <Stack align='center' spacing={0}>
    {children.length ? (
      <Text
        fontWeight='600'
        padding='8px'
        textTransform='uppercase'
        fontSize='0.875rem'
        letterSpacing='0.05em'
      >
        {text}
      </Text>
    ) : null}
    {children.length ? (
      children.map((child) => (
        <Link
          key={child.text}
          py={2}
          to={child.url}
          href={child.file?.asset?.url && child.file.asset.url}
          color='black'
          fontSize='lg'
          textAlign='center'
          textDecoration={isActive({ url: child.url }, currentLocation) ? 'underline' : 'none'}
        >
          {child.text}
        </Link>
      ))
    ) : (
      <Link
        py={2}
        to={url}
        color='black'
        fontSize='lg'
        textAlign='center'
        textDecoration={isActive({ url }, currentLocation) ? 'underline' : 'none'}
      >
        {text}
      </Link>
    )}
  </Stack>
);

const MobileNav = ({ currentLocation }) => {
  const data = useStaticQuery(NAVIGATION_QUERY);
  const { items } = data.data;
  return (
    <Stack
      bg='white'
      pt={30}
      pb='12.5rem'
      px={20}
      display={{ base: 'flex', xl: 'none' }}
      alignSelf='center'
      spacing={20}
      divider={<StackDivider borderColor='gray.200' />}
      color='black'
      height='100vh'
      overflowY='scroll'
      className='nav'
    >
      {items.map((navItem) => (
        <MobileNavItem key={navItem.text} {...navItem} currentLocation={currentLocation} />
      ))}
      <Box textAlign='center'>
        <Text
          fontWeight='600'
          padding='8px'
          textTransform='uppercase'
          fontSize='0.875rem'
          letterSpacing='0.05em'
        >
          Download the Report
        </Text>
        <DownloadLinks
          textAlign='center'
          display='grid'
          gridTemplateColumns='1fr'
          variant='header'
        />
      </Box>
    </Stack>
  );
};

const NAVIGATION_QUERY = graphql`
  query Navigation {
    data: sanityMeta {
      items: navItems {
        url: navItemUrl {
          internal: internalLink {
            slug {
              current
            }
            section
          }
          external: externalUrl
        }
        children {
          url: navItemUrl {
            external: externalUrl
            internal: internalLink {
              slug {
                current
              }
              section
            }
          }
          text
          file {
            asset {
              url
            }
          }
        }
        text
        file {
          asset {
            url
          }
        }
      }
    }
  }
`;
